import { useCallback, useEffect, useState } from 'react'
import { api, type Erinnerung, type Termin, type TerminEingabe } from '../api'
import { link } from '../navigation'
import { tagesDatum } from '../tagebuch'
import { abstand, terminZeit, vergangeneEintraege } from '../termine'
import { Erinnerungen, ErledigteErinnerung } from './Erinnerungen'

const eigeneZeitzone = () => Intl.DateTimeFormat().resolvedOptions().timeZone

export function Termine({ onGeaendert }: { onGeaendert: () => void }) {
  const [daten, setDaten] = useState<{ anstehend: Termin[]; vergangen: Termin[] } | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)
  const [erledigte, setErledigte] = useState<Erinnerung[]>([])
  const [vergangeneZeigen, setVergangeneZeigen] = useState(false)

  const laden = useCallback(
    () =>
      api
        .termine()
        .then((d) => {
          setDaten(d)
          setFehler(null)
        })
        .catch((e) => setFehler((e as Error).message)),
    [],
  )

  const erledigteLaden = useCallback(() => api.erinnerungenErledigt().then(setErledigte).catch(() => {}), [])

  useEffect(() => {
    laden()
    erledigteLaden()
  }, [laden, erledigteLaden])

  const geaendert = async () => {
    await Promise.all([laden(), erledigteLaden()])
    onGeaendert()
  }

  if (!daten) return fehler ? <p className="fehler">{fehler}</p> : null

  const vorschlaege = daten.anstehend.filter((t) => t.status === 'VORGESCHLAGEN')
  const bestaetigt = daten.anstehend.filter((t) => t.status === 'BESTAETIGT')
  const heute = tagesDatum(new Date(), eigeneZeitzone())
  const vergangene = vergangeneEintraege(daten.vergangen, erledigte)

  return (
    <div className="termine">
      {fehler && <p className="fehler">{fehler}</p>}

      <Erinnerungen onGeaendert={geaendert} />

      {vorschlaege.length > 0 && (
        <section>
          <h2 className="vorschlaege">{vorschlaege.length === 1 ? 'Ein Vorschlag' : `${vorschlaege.length} Vorschläge`} aus deinen Notizen</h2>
          {vorschlaege.map((t) => (
            <TerminKarte key={t.id} termin={t} heute={heute} onGeaendert={geaendert} />
          ))}
        </section>
      )}

      <section>
        <h2>Anstehend</h2>
        {bestaetigt.length === 0 && (
          <p className="leise">
            Keine Termine. Erzähl in einer Notiz von einer Verabredung, dann erscheint sie hier als Vorschlag.
          </p>
        )}
        {bestaetigt.map((t) => (
          <TerminKarte key={t.id} termin={t} heute={heute} onGeaendert={geaendert} />
        ))}
      </section>

      {vergangene.length > 0 && (
        <section>
          <button className="link" onClick={() => setVergangeneZeigen(!vergangeneZeigen)}>
            {vergangeneZeigen ? 'Vergangene ausblenden' : `Vergangene zeigen (${vergangene.length})`}
          </button>
          {vergangeneZeigen &&
            vergangene.map((v) =>
              v.art === 'termin' ? (
                <TerminKarte key={`t${v.termin.id}`} termin={v.termin} heute={heute} onGeaendert={geaendert} />
              ) : (
                <ErledigteErinnerung key={`e${v.erinnerung.id}`} erinnerung={v.erinnerung} onGeaendert={geaendert} />
              ),
            )}
        </section>
      )}
    </div>
  )
}

function TerminKarte({ termin: t, heute, onGeaendert }: { termin: Termin; heute: string; onGeaendert: () => void }) {
  const [bearbeiten, setBearbeiten] = useState(false)
  const [fehler, setFehler] = useState<string | null>(null)

  const aktion = async (f: () => Promise<unknown>) => {
    setFehler(null)
    try {
      await f()
      onGeaendert()
    } catch (e) {
      setFehler((e as Error).message)
    }
  }

  if (bearbeiten) {
    return (
      <TerminFormular
        termin={t}
        onAbbrechen={() => setBearbeiten(false)}
        onSpeichern={(e) =>
          aktion(async () => {
            await api.terminAendern(t.id, e)
            setBearbeiten(false)
          })
        }
      />
    )
  }

  const vorschlag = t.status === 'VORGESCHLAGEN'
  return (
    <article className={`karte termin${vorschlag ? ' vorschlag' : ''}`}>
      <header>
        <time dateTime={t.beginn}>{terminZeit(t)}</time>
        <span className="leise klein">{abstand(t.datum, heute)}</span>
      </header>
      <p className="titel">{t.titel}</p>
      {t.ort && <p className="leise klein">📍 {t.ort}</p>}
      {(t.personen.length > 0 || t.notizId) && (
        <p className="chips">
          {t.personen.map((p) => (
            <a key={p.id} className="chip person" href={link.person(p.id)}>
              {p.name}
            </a>
          ))}
        </p>
      )}
      {fehler && <p className="fehler">{fehler}</p>}
      <footer>
        {vorschlag ? (
          <>
            <button onClick={() => aktion(() => api.terminBestaetigen(t.id))}>Übernehmen</button>
            <button className="zweitrangig" onClick={() => setBearbeiten(true)}>
              Anpassen
            </button>
            <button className="link" onClick={() => aktion(() => api.terminVerwerfen(t.id))}>
              Verwerfen
            </button>
          </>
        ) : (
          <>
            <a className="knopf" href={api.terminKalenderUrl(t.id)} download>
              In Kalender
            </a>
            <button className="link" onClick={() => setBearbeiten(true)}>
              Bearbeiten
            </button>
            <button
              className="link gefahr"
              onClick={() => confirm(`„${t.titel}“ löschen?`) && aktion(() => api.terminLoeschen(t.id))}
            >
              Löschen
            </button>
          </>
        )}
      </footer>
    </article>
  )
}

function TerminFormular({
  termin,
  onSpeichern,
  onAbbrechen,
}: {
  termin: Termin
  onSpeichern: (e: TerminEingabe) => void
  onAbbrechen: () => void
}) {
  const dauerAlt = termin.ende ? Math.round((Date.parse(termin.ende) - Date.parse(termin.beginn)) / 60000) : null
  const [titel, setTitel] = useState(termin.titel)
  const [datum, setDatum] = useState(termin.datum)
  const [uhrzeit, setUhrzeit] = useState(termin.uhrzeit?.substring(0, 5) ?? '')
  const [dauer, setDauer] = useState(dauerAlt ? String(dauerAlt) : '')
  const [ort, setOrt] = useState(termin.ort)

  return (
    <form
      className="karte formular"
      onSubmit={(e) => {
        e.preventDefault()
        onSpeichern({ titel, datum, uhrzeit: uhrzeit || null, dauerMinuten: uhrzeit && dauer ? Number(dauer) : null, ort })
      }}
    >
      <label>
        Titel
        <input value={titel} onChange={(e) => setTitel(e.target.value)} required />
      </label>
      <div className="zeile">
        <label>
          Datum
          <input type="date" value={datum} onChange={(e) => setDatum(e.target.value)} required />
        </label>
        <label>
          Uhrzeit
          <input type="time" value={uhrzeit} onChange={(e) => setUhrzeit(e.target.value)} />
        </label>
      </div>
      {uhrzeit ? (
        <label>
          Dauer in Minuten
          <input type="number" min={5} step={5} value={dauer} onChange={(e) => setDauer(e.target.value)} placeholder="z. B. 90" />
        </label>
      ) : (
        <p className="leise klein">Ohne Uhrzeit ist der Termin ganztägig.</p>
      )}
      <label>
        Ort
        <input value={ort} onChange={(e) => setOrt(e.target.value)} />
      </label>
      <div className="knoepfe">
        <button type="button" className="zweitrangig" onClick={onAbbrechen}>
          Abbrechen
        </button>
        <button type="submit">{termin.status === 'VORGESCHLAGEN' ? 'Übernehmen' : 'Speichern'}</button>
      </div>
    </form>
  )
}
